import { useState, useContext, useEffect } from "react";
import { useNotification } from "../contexts/NotificationContext";
import {
  CreditCard,
  X,
  Calendar,
  Zap,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowRight,
  Download
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LoginContext } from "../LoginContext";
import { useTranslation } from "react-i18next";
import apiService from "../backend/api";
import { openBillingPortal } from "../services/stripeService";
import useSubscription from "../hooks/useSubscription";
import usePlans from "../hooks/usePlans";
import SubscriptionSkeleton from "./skeletons/SubscriptionSkeleton";
import { useQueryClient } from "@tanstack/react-query";

export default function ProfileSubscription() {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { loginInfo } = useContext(LoginContext);
  const { t } = useTranslation();
  const loggedInUser = loginInfo?.username;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);

  // Use React Query custom hook
  const { data: subData, isLoading, isError } = useSubscription(loggedInUser);

  // Auto-refresh subscription data when returning from Billing Portal or payment failed email
  useEffect(() => {
    const fromBillingPortal = searchParams.get('billingPortalReturn');
    const fromPaymentFailed = searchParams.get('paymentFailed');

    if (fromBillingPortal || fromPaymentFailed) {
      // Invalidate cache to fetch fresh data
      queryClient.invalidateQueries(["subscription", loggedInUser]);

      // Clean up URL params
      searchParams.delete('billingPortalReturn');
      searchParams.delete('paymentFailed');
      setSearchParams(searchParams, { replace: true });

      if (fromBillingPortal) {
        showNotification(t("profile.sub.paymentUpdated", "Zahlungsdaten wurden aktualisiert. Ihr Abonnement-Status wird in Kürze aktualisiert."), 'success');
      }
    }
  }, [searchParams, setSearchParams, queryClient, loggedInUser, showNotification]);

  const subscription = subData?.subscription;
  const planName = subscription?.planName || "Free";
  const planStatus = subscription?.status || "inaktiv";
  const billingPeriod = subscription?.billingPeriod || "Monatlich";
  const nextBillingDate = subscription?.nextBillingDate ? new Date(subscription.nextBillingDate).toLocaleDateString('de-DE') : "-";
  const subscriptionDownloads = subscription?.remainingDownloads || subscription?.downloadsRemaining || 0;

  // Plan-basierter Lookup für Downloads pro Monat (dynamisch aus DB via usePlans)
  const { data: allPlansData } = usePlans();
  const planBasedDownloads = allPlansData?.find(p => p.name === planName && p.type === 'subscription')?.downloads || 0;

  // Nutze downloadsPerMonth, aber falls es kleiner ist als remainingDownloads oder
  // bei "wechselnd" Status (wo der alte Plan noch gilt), nutze den Plan-Lookup
  const rawDownloadsPerMonth = subscription?.downloadsPerMonth || 0;
  const subscriptionTotal = (rawDownloadsPerMonth < subscriptionDownloads || planStatus === "wechselnd")
    ? planBasedDownloads
    : rawDownloadsPerMonth;
  const bonusDownloads = subData?.bonusDownloads || 0;
  const packageDownloads = subData?.packageDownloads || 0;
  const scheduledChange = subscription?.scheduledChange || null;
  const cancellationDate = subscription?.cancellationDate ? new Date(subscription.cancellationDate).toLocaleDateString('de-DE') : null;

  const getSubscriptionStatusColor = () => {
    switch (planStatus.toLowerCase()) {
      case "aktiv":
        return "bg-[#0f7699] text-white";
      case "auslaufend":
        return "bg-amber-500 text-white";
      case "wechselnd":
        return "bg-blue-500 text-white";
      case "inaktiv":
        return "bg-red-500 text-white";
      case "gekündigt":
        return "bg-amber-500 text-white";
      case "geplant":
        return "bg-purple-500 text-white";
      case "payment_failed":
        return "bg-red-500 text-white";
      default:
        return "bg-[#0f7699] text-white";
    }
  };

  const handleCancelSubscription = () => {
    setShowCancelModal(true);
  };

  const confirmCancellation = async () => {
    if (!loggedInUser) return;

    try {
      const data = await apiService.request(`/api/payment/cancel-subscription`, {
        method: 'POST',
        body: JSON.stringify({
          username: loggedInUser
        }),
      });

      if (!data.error) {
        showNotification(t("profileSubscription.subscriptionCancelledSuccess", "Abonnement wurde erfolgreich gekündigt!"), 'success');
        setShowCancelModal(false);
        queryClient.invalidateQueries(["subscription", loggedInUser]);
      } else {
        showNotification(t("profile.sub.cancelError", "Fehler beim Kündigen") + ': ' + (data.message || data.error || t("profile.sub.unknownError", "Unbekannter Fehler")), 'error');
      }
    } catch (error) {
      console.error('Fehler beim Kündigen des Abonnements:', error);
      showNotification(t("profile.sub.cancelError", "Fehler beim Kündigen") + ': ' + error.message, 'error');
    }
  };

  const cancelCancellation = () => {
    setShowCancelModal(false);
  };

  const handleUpdatePaymentMethod = async () => {
    if (!loggedInUser || billingPortalLoading) return;
    setBillingPortalLoading(true);
    try {
      await openBillingPortal(loggedInUser);
    } catch (error) {
      showNotification(t("profile.sub.billingPortalError", "Fehler beim Öffnen des Zahlungsportals") + ': ' + error.message, 'error');
      setBillingPortalLoading(false);
    }
  };

  if (isLoading) return <SubscriptionSkeleton />;

  if (isError) return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="bg-red-50 dark:bg-red-900/20 p-5 sm:p-8 md:p-10 rounded-[2.5rem] border border-red-100 dark:border-red-800 text-red-700 dark:text-red-300 text-center font-bold">
        {t("profileSubscription.errorLoadingSubscription", "Fehler beim Laden der Abonnementdaten.")}
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      {/* Compact Header */}
      <div className="flex items-center gap-3 sm:gap-4 mb-6">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/10 rounded-xl sm:rounded-2xl flex items-center justify-center text-[#0f7699] dark:text-[#4fc3f7]">
          <CreditCard size={20} className="sm:hidden" /><CreditCard size={24} className="hidden sm:block" />
        </div>
        <div>
          <h1 className="text-lg sm:text-2xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
            {t("profileSubscription.subscriptionDetails", "Abonnementdetails")}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
            {t("profileSubscription.loggedInAs", "Angemeldet als:")} <span className="font-bold">{loginInfo?.username}</span>
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2rem] shadow-sm border border-slate-200 dark:border-slate-700 p-5 sm:p-8 md:p-10 transition-colors duration-300">
          <div className="space-y-6 sm:space-y-8 md:space-y-12">
            <div>
              <div className="space-y-6 sm:space-y-8 md:space-y-10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                  <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] dark:text-[#4fc3f7] opacity-70">{t("profileSubscription.currentPlan", "Aktueller Plan")}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-slate-800 dark:text-white">{planName}</span>
                      <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-lg ${getSubscriptionStatusColor()}`}>
                        {planStatus}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] dark:text-[#4fc3f7] opacity-70">{t("profileSubscription.billingPeriod", "Abrechnungszeitraum")}</p>
                    <p className="text-lg font-bold text-slate-800 dark:text-white">{billingPeriod || "-"}</p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-[#0f7699] dark:text-[#4fc3f7] opacity-70">
                      {planStatus === "gekündigt" ? t("profileSubscription.usableUntil", "Nutzbar bis") : t("profileSubscription.nextBillingDate", "Nächstes Abrechnungsdatum")}
                    </p>
                    <p className="text-lg font-bold text-slate-800 dark:text-white">{nextBillingDate || "-"}</p>
                  </div>
                </div>

                {/* Credit Balance - Stacked Bar */}
                {(() => {
                  const subscriptionUsed = Math.max(0, subscriptionTotal - subscriptionDownloads);
                  const totalAvailable = subscriptionDownloads + bonusDownloads + packageDownloads;
                  const totalAll = subscriptionTotal + bonusDownloads + packageDownloads;
                  const maxBar = Math.max(50, totalAll);

                  return (
                    <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl sm:rounded-[2rem] p-4 sm:p-6 relative overflow-hidden">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Download size={18} className="text-[#0f7699]" />
                          <h3 className="text-xs font-black text-slate-800 dark:text-white uppercase tracking-widest">{t("profile.sub.credits", "Guthaben")}</h3>
                        </div>
                        <div className="text-2xl font-black text-slate-900 dark:text-white">{totalAvailable} <span className="text-sm font-normal text-slate-500">{t("profile.sub.available", "verfügbar")}</span></div>
                      </div>

                      {/* Stacked Credit Bar - mit Tooltip bei Hover */}
                      <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden flex mb-4">
                        {/* Paket-Guthaben */}
                        {packageDownloads > 0 && (
                          <div
                            className="bg-indigo-500 h-full transition-all duration-500 cursor-pointer hover:brightness-110"
                            style={{ width: `${(packageDownloads / maxBar) * 100}%` }}
                            title={t("profile.sub.packageCreditsTooltip", "Paket-Guthaben: {{count}} verfügbar (kein Ablaufdatum)", { count: packageDownloads })}
                          />
                        )}
                        {/* Bonus-Guthaben */}
                        {bonusDownloads > 0 && (
                          <div
                            className="bg-amber-400 h-full transition-all duration-500 cursor-pointer hover:brightness-110"
                            style={{ width: `${(bonusDownloads / maxBar) * 100}%` }}
                            title={t("profile.sub.bonusCreditsTooltip", "Bonus-Guthaben: {{count}} verfügbar (kein Ablaufdatum)", { count: bonusDownloads })}
                          />
                        )}
                        {/* Abo-Guthaben (verfügbar) */}
                        {subscriptionDownloads > 0 && (
                          <div
                            className="bg-cyan-500 h-full transition-all duration-500 cursor-pointer hover:brightness-110"
                            style={{ width: `${(subscriptionDownloads / maxBar) * 100}%` }}
                            title={t("profile.sub.subCreditsTooltip", "Abo-Guthaben: {{available}} von {{total}} verfügbar\nNutzbar bis: {{date}}", { available: subscriptionDownloads, total: subscriptionTotal, date: nextBillingDate })}
                          />
                        )}
                        {/* Abo-Guthaben (verbraucht - grau) */}
                        {subscriptionUsed > 0 && (
                          <div
                            className="bg-slate-400 h-full transition-all duration-500 relative cursor-pointer hover:bg-slate-500"
                            style={{ width: `${(subscriptionUsed / maxBar) * 100}%` }}
                            title={t("profile.sub.subUsedTooltip", "Abo verbraucht: {{used}} von {{total}}\nNächste Aufladung: {{date}}", { used: subscriptionUsed, total: subscriptionTotal, date: nextBillingDate })}
                          >
                            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white/80">
                              {subscriptionUsed > 3 ? `-${subscriptionUsed}` : ''}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Credit Breakdown - mit verfügbar/gesamt Anzeige */}
                      <div className="space-y-2 mb-4">
                        {/* Abo-Guthaben */}
                        <div
                          className="flex items-center justify-between p-2.5 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800/30 rounded-xl cursor-pointer hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-colors group"
                          title={t("profile.sub.usableUntil", "Nutzbar bis: {{date}}", { date: nextBillingDate })}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-cyan-500" />
                            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">{t("profile.sub.subCredits", "Abo-Guthaben")}</span>
                            <span className="text-[8px] px-1.5 py-0.5 bg-cyan-100 dark:bg-cyan-800/50 text-cyan-600 dark:text-cyan-400 rounded font-bold">1.</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-black text-cyan-600 dark:text-cyan-400">{subscriptionDownloads}</span>
                            {subscriptionTotal > 0 && (
                              <span className="text-[10px] text-slate-500 dark:text-slate-500">/ {subscriptionTotal}</span>
                            )}
                            {subscriptionUsed > 0 && (
                              <span className="text-[9px] text-slate-400 dark:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                ({subscriptionUsed} {t("profile.sub.used", "verbraucht")})
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Bonus-Guthaben */}
                        <div
                          className="flex items-center justify-between p-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-xl cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                          title={t("profile.sub.bonusNoExpiry", "Bonus-Guthaben: Kein Ablaufdatum")}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">{t("profile.sub.bonusCredits", "Bonus-Guthaben")}</span>
                            <span className="text-[8px] px-1.5 py-0.5 bg-amber-100 dark:bg-amber-800/50 text-amber-600 dark:text-amber-400 rounded font-bold">2.</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-black text-amber-600 dark:text-amber-400">{bonusDownloads}</span>
                            <span className="text-[10px] text-slate-400 dark:text-slate-600">∞</span>
                          </div>
                        </div>

                        {/* Paket-Guthaben */}
                        <div
                          className="flex items-center justify-between p-2.5 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/30 rounded-xl cursor-pointer hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                          title={t("profile.sub.packageNoExpiry", "Paket-Guthaben: Kein Ablaufdatum")}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />
                            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">{t("profile.sub.packageCredits", "Paket-Guthaben")}</span>
                            <span className="text-[8px] px-1.5 py-0.5 bg-indigo-100 dark:bg-indigo-800/50 text-indigo-600 dark:text-indigo-400 rounded font-bold">3.</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-black text-indigo-600 dark:text-indigo-400">{packageDownloads}</span>
                            <span className="text-[10px] text-slate-400 dark:text-slate-600">∞</span>
                          </div>
                        </div>
                      </div>

                      <div className="text-[10px] text-slate-500 p-2 bg-slate-100 dark:bg-slate-800/50 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 sm:gap-0">
                        <span><span className="font-semibold text-slate-600 dark:text-slate-400">{t("profile.sub.usageOrder", "Nutzungsreihenfolge")}:</span> {t("profile.sub.usageOrderDesc", "Zuerst Abo → dann Bonus → zuletzt Paket")}</span>
                        {subscriptionTotal > 0 && (
                          <span className="text-cyan-600 dark:text-cyan-400 font-medium">{t("profile.sub.subReset", "Abo-Reset")}: {nextBillingDate}</span>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Geplante Änderungen Banner */}
                {planStatus === "geplant" && subscription?.scheduledActivation && (
                  <div className="p-6 bg-purple-50 dark:bg-purple-900/20 rounded-[1.5rem] border border-purple-100 dark:border-purple-800/30 flex gap-5 animate-in fade-in slide-in-from-top-4">
                    <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center text-purple-600 dark:text-purple-400 shadow-sm shrink-0">
                      <Calendar size={24} />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-sm font-black text-purple-900 dark:text-purple-300 uppercase tracking-wide">
                        {t("profileSubscription.subscriptionScheduled", "Abonnement geplant")}
                      </h4>
                      <p className="text-sm text-purple-700 dark:text-purple-400 font-medium">
                        {t("profileSubscription.newSubscriptionActivation", "Ihr neues {{planName}}-Abonnement wird am {{date}} aktiviert.", {
                          planName,
                          date: new Date(subscription.scheduledActivation.activationDate).toLocaleDateString('de-DE')
                        })}
                      </p>
                      <p className="text-xs text-purple-500 dark:text-purple-400/70 font-bold mt-2">
                        {t("profileSubscription.reason", "Grund:")} {subscription.scheduledActivation.reason}
                      </p>
                    </div>
                  </div>
                )}

                {/* Kündigung Banner */}
                {planStatus === "gekündigt" && (
                  <div className="p-4 sm:p-6 md:p-8 bg-amber-50 dark:bg-amber-900/20 rounded-2xl sm:rounded-[2rem] border border-amber-100 dark:border-amber-800/30 space-y-4 sm:space-y-6 md:space-y-8 animate-in fade-in zoom-in-95">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center text-amber-600 dark:text-amber-400 shadow-sm">
                        <AlertCircle size={24} />
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-amber-900 dark:text-amber-300 uppercase tracking-wide">
                          {t("profileSubscription.subscriptionCancelled", "Abo gekündigt")}
                        </h4>
                        {cancellationDate && (
                          <p className="text-xs text-amber-600 dark:text-amber-400/70 font-bold uppercase tracking-widest">
                            {t("profileSubscription.cancelledOn", "gekündigt am")} {cancellationDate}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
                      <div className="bg-white/60 dark:bg-slate-800/60 p-4 sm:p-6 rounded-2xl text-center border border-white dark:border-slate-700">
                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-900/50 dark:text-amber-400/50 mb-1">{t("profileSubscription.downloadsAvailable", "Downloads")}</p>
                        <p className="text-2xl sm:text-3xl font-black text-amber-600 dark:text-amber-400">{subscriptionDownloads}</p>
                      </div>
                      <div className="bg-white/60 dark:bg-slate-800/60 p-4 sm:p-6 rounded-2xl text-center border border-white dark:border-slate-700">
                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-900/50 dark:text-amber-400/50 mb-1">{t("profileSubscription.usableUntilShort", "Nutzbar bis")}</p>
                        <p className="text-lg sm:text-xl font-black text-amber-600 dark:text-amber-400">{nextBillingDate}</p>
                      </div>
                      <div className="bg-white/60 dark:bg-slate-800/60 p-4 sm:p-6 rounded-2xl text-center border border-white dark:border-slate-700">
                        <p className="text-[10px] font-black uppercase tracking-widest text-amber-900/50 dark:text-amber-400/50 mb-1">{t("profileSubscription.currentPlan", "Plan")}</p>
                        <p className="text-base sm:text-lg font-black text-amber-600 dark:text-amber-400">{planName}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <p className="text-sm text-amber-900/80 dark:text-amber-300 font-bold leading-relaxed">
                        {t("profileSubscription.whatHappensNext", "Was passiert als nächstes")}:
                      </p>
                      <ul className="space-y-2 text-sm text-amber-900/60 dark:text-amber-400/70 font-medium">
                        <li className="flex items-start gap-3">
                          <ArrowRight size={14} className="mt-1 shrink-0" />
                          <span>{t("profileSubscription.remainingDownloadsUsable", "Ihre {{count}} verbleibenden Downloads können Sie bis zum {{date}} nutzen", { count: subscriptionDownloads, date: nextBillingDate })}</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <ArrowRight size={14} className="mt-1 shrink-0" />
                          <span>{t("profileSubscription.accountSwitchedToFree", "Ab dem {{date}} wird Ihr Konto automatisch auf den Free-Plan umgestellt", { date: nextBillingDate })}</span>
                        </li>
                      </ul>
                    </div>

                    <div className="flex flex-wrap gap-4 pt-4 border-t border-amber-200/50 dark:border-amber-800/30">
                      <button
                        onClick={() => navigate('/pakete')}
                        className="px-6 h-12 bg-[#0f7699] text-white rounded-xl font-bold text-xs uppercase tracking-widest shadow-lg shadow-blue-900/10 hover:bg-[#0d6482] transition-all"
                      >
                        {t("profile.sub.newSubscription", "Neues Abo abschließen")}
                      </button>
                      <button
                        onClick={() => navigate('/upload')}
                        className="px-6 h-12 bg-white dark:bg-slate-800 text-amber-700 dark:text-amber-400 rounded-xl font-bold text-xs uppercase tracking-widest border-2 border-amber-200 dark:border-amber-800/50 hover:bg-amber-100/50 dark:hover:bg-amber-900/30 transition-all"
                      >
                        {t("profile.sub.useDownloads", "Downloads nutzen")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Geplanter Wechsel Banner */}
                {scheduledChange && (
                  <div className="p-4 sm:p-6 md:p-8 bg-blue-50 dark:bg-blue-900/20 rounded-2xl sm:rounded-[2rem] border border-blue-100 dark:border-blue-800/30 space-y-4 sm:space-y-6 animate-in fade-in slide-in-from-top-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center text-blue-600 dark:text-blue-400 shadow-sm">
                        <Clock size={24} />
                      </div>
                      <h4 className="text-sm font-black text-blue-900 dark:text-blue-300 uppercase tracking-wide">
                        {t("profileSubscription.scheduledPlanChange", "Geplanter Abo-Wechsel")}
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 bg-white/60 dark:bg-slate-800/60 p-4 sm:p-6 rounded-2xl border border-white dark:border-slate-700">
                      <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase tracking-widest text-blue-900/40 dark:text-blue-400/40">{t("profileSubscription.changeTo", "Wechsel zu")}</p>
                        <p className="text-lg font-black text-blue-700 dark:text-blue-400">{scheduledChange.newPlanName}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase tracking-widest text-blue-900/40 dark:text-blue-400/40">{t("profileSubscription.changeOn", "Wechsel am")}</p>
                        <p className="text-lg font-black text-blue-700 dark:text-blue-400">{new Date(scheduledChange.effectiveDate).toLocaleDateString("de-DE")}</p>
                      </div>
                    </div>

                    <div className="p-6 bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 rounded-2xl border border-[#0f7699]/10 dark:border-[#4fc3f7]/10">
                      <div className="flex items-start gap-4">
                        <CheckCircle2 size={18} className="text-[#0f7699] dark:text-[#4fc3f7] shrink-0 mt-0.5" />
                        <p className="text-sm text-[#0f7699] dark:text-[#4fc3f7] font-medium leading-relaxed italic">
                          {t("profileSubscription.downloadsAvailableUntilChange", "Ihr aktuelles Abo bleibt bis zum Wechsel aktiv. Alle aktuellen Downloads ({{count}}) bleiben verfügbar.", { count: subscriptionDownloads })}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => navigate('/pakete')}
                      className="px-6 h-12 bg-[#0f7699] text-white rounded-xl font-bold text-xs uppercase tracking-widest shadow-lg shadow-blue-900/10 hover:bg-[#0d6482] transition-all"
                    >
                      {t("profile.sub.choosePlan", "Anderen Plan wählen")}
                    </button>
                  </div>
                )}

                {/* Payment Failed Banner */}
                {(subscription?.status === 'payment_failed' || subscription?.paymentFailedCount > 0) && (
                  <div className="p-4 sm:p-6 md:p-8 bg-red-50 dark:bg-red-900/20 rounded-2xl sm:rounded-[2rem] border border-red-200 dark:border-red-800/30 space-y-4 sm:space-y-6 animate-in fade-in slide-in-from-top-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center text-red-600 dark:text-red-400 shadow-sm">
                        <AlertCircle size={24} />
                      </div>
                      <h4 className="text-sm font-black text-red-900 dark:text-red-300 uppercase tracking-wide">
                        {subscription?.status === 'payment_failed' ? t("profile.sub.subscriptionLocked", "Abonnement gesperrt") : t("profile.sub.paymentFailed", "Zahlung fehlgeschlagen")}
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 bg-white/60 dark:bg-slate-800/60 p-4 sm:p-6 rounded-2xl border border-white dark:border-slate-700">
                      <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase tracking-widest text-red-900/40 dark:text-red-400/40">{t("profile.sub.status", "Status")}</p>
                        <p className="text-lg font-black text-red-700 dark:text-red-400">
                          {subscription?.status === 'payment_failed' ? t("profile.sub.locked", "Gesperrt") : t("profile.sub.attempt", "Versuch {{count}}/3", { count: subscription?.paymentFailedCount })}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-[10px] font-black uppercase tracking-widest text-red-900/40 dark:text-red-400/40">{t("profile.sub.since", "Seit")}</p>
                        <p className="text-lg font-black text-red-700 dark:text-red-400">
                          {subscription?.paymentFailedAt ? new Date(subscription.paymentFailedAt).toLocaleDateString("de-DE") : '—'}
                        </p>
                      </div>
                    </div>

                    <div className="p-4 sm:p-6 bg-red-100/50 dark:bg-red-900/30 rounded-2xl border border-red-200/50 dark:border-red-800/30">
                      <div className="flex items-start gap-4">
                        <AlertCircle size={18} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700 dark:text-red-300 font-medium leading-relaxed">
                          {subscription?.status === 'payment_failed'
                            ? t("profile.sub.lockedDesc", "Ihr Abonnement wurde nach mehreren fehlgeschlagenen Zahlungsversuchen gesperrt. Bitte aktualisieren Sie Ihre Zahlungsmethode, um Ihr Abo wieder zu aktivieren.")
                            : t("profile.sub.paymentFailedDesc", "Die letzte Zahlung konnte nicht verarbeitet werden. Bitte überprüfen Sie Ihre Zahlungsmethode, um eine Sperrung Ihres Abonnements zu vermeiden.")
                          }
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={handleUpdatePaymentMethod}
                      disabled={billingPortalLoading}
                      className="px-5 sm:px-8 h-11 sm:h-14 bg-red-600 text-white rounded-2xl font-bold text-xs sm:text-sm tracking-widest shadow-xl shadow-red-900/20 hover:bg-red-700 transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <AlertCircle size={18} />
                      <span>{billingPortalLoading ? t("profile.sub.loading", "Wird geladen...") : t("profile.sub.updatePayment", "Zahlungsmethode aktualisieren")}</span>
                    </button>
                  </div>
                )}

                {/* Actions Section */}
                <div className="pt-6 sm:pt-8 md:pt-10 border-t border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                  <button
                    onClick={() => navigate('/pakete')}
                    className="px-5 sm:px-8 h-11 sm:h-14 bg-[#0f7699] text-white rounded-2xl font-bold text-xs sm:text-sm tracking-widest shadow-xl shadow-blue-900/10 hover:bg-[#0d6482] transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3"
                  >
                    <Zap size={18} />
                    <span>{t("profile.sub.managePackages", "Pakete verwalten")}</span>
                  </button>

                  {subscription && subscription.planName !== "Free" && subscription.status !== "gekündigt" && (
                    <button
                      onClick={handleCancelSubscription}
                      className="px-5 sm:px-8 h-11 sm:h-14 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-2xl font-bold text-xs sm:text-sm tracking-widest border-2 border-red-100 dark:border-red-800/50 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all active:scale-[0.98] flex items-center justify-center gap-3"
                    >
                      <X size={18} />
                      <span>{t("profile.sub.cancelSubscription", "Abonnement kündigen")}</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={cancelCancellation}></div>
          <div className="relative w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl sm:rounded-[2.5rem] shadow-2xl border border-white/20 dark:border-slate-700 p-5 sm:p-8 md:p-10 animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-2xl flex items-center justify-center text-red-600 dark:text-red-400 mb-6">
              <AlertCircle size={32} />
            </div>

            <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight mb-4">
              {t("profile.sub.cancelSubscription", "Abonnement kündigen")}
            </h3>

            <div className="space-y-4 mb-6 sm:mb-10">
              <p className="text-slate-600 dark:text-slate-300 font-medium">
                {t("profile.sub.cancelConfirmQuestion", "Sind Sie sicher, dass Sie Ihr Abonnement kündigen möchten?")}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest leading-relaxed">
                {t("profile.sub.cancelNote", "Nach der Kündigung können Sie Ihre verbleibenden Downloads bis zum Ende der aktuellen Abrechnungsperiode nutzen.")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={cancelCancellation}
                className="h-14 bg-slate-50 dark:bg-slate-700 text-slate-500 dark:text-slate-300 rounded-2xl font-black text-xs uppercase tracking-widest border border-slate-100 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 transition-all"
              >
                {t("profileSubscription.cancel", "Abbrechen")}
              </button>
              <button
                onClick={confirmCancellation}
                className="h-14 bg-red-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-red-900/20 hover:bg-red-700 transition-all"
              >
                {t("profile.sub.confirmCancel", "Kündigen bestätigen")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
